import type {
  ChangeEvent,
  CoverageDeclaration,
  Evaluation,
  IncidentBrief,
  MonitorCompilation,
  Observation,
  Scope,
  VerificationReceipt,
} from "@facet/core";

export interface ServiceSummary {
  id: string;
  name: string;
  createdAt: string;
}

export interface CoverageRecord {
  service: ServiceSummary;
  declaration: CoverageDeclaration;
  compilation: MonitorCompilation;
}

export type NotificationDeliveryStatus =
  | "pending"
  | "attempting"
  | "accepted"
  | "sent"
  | "delayed"
  | "delivered"
  | "failed"
  | "unknown";

export interface NotificationRouteRecord {
  id: string;
  monitorId: string;
  channel: "email";
  destination: string;
  enabled: boolean;
  createdAt: string;
  latestDelivery: {
    notificationId: string;
    status: NotificationDeliveryStatus;
    occurredAt: string;
  } | null;
}

export interface IncidentDetail {
  incident: IncidentBrief;
  evaluations: readonly Evaluation[];
  observations: readonly Observation[];
  changes: readonly ChangeEvent[];
}

export interface VerificationReceiptSummary {
  receipt: VerificationReceipt;
  scope: Scope;
}

export interface ChangeVerificationSummary {
  change: ChangeEvent;
  receipts: readonly VerificationReceiptSummary[];
  truncated: boolean;
}

export interface ChangeVerificationDetail {
  change: ChangeEvent;
  items: readonly (VerificationReceiptSummary & {
    evaluations: readonly Evaluation[];
    observations: readonly Observation[];
  })[];
  truncated: boolean;
}

interface ApiErrorBody {
  message?: string;
  issues?: readonly string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  if (response.status === 401) {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    return new Promise<T>(() => undefined);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ApiErrorBody;
    const detail = body.issues?.filter(Boolean).join(" ") || body.message;
    throw new Error(detail || `Request failed with HTTP ${response.status}.`);
  }
  return response.json() as Promise<T>;
}

export async function loadCoverage(signal?: AbortSignal): Promise<readonly CoverageRecord[]> {
  const response = await request<{ items: CoverageRecord[] }>("/v1/coverage", { signal: signal ?? null });
  return response.items;
}

export async function loadNotificationRoutes(
  signal?: AbortSignal,
): Promise<readonly NotificationRouteRecord[]> {
  const response = await request<{ items: NotificationRouteRecord[] }>("/v1/notification-routes", {
    signal: signal ?? null,
  });
  return response.items;
}

export async function loadIncidents(signal?: AbortSignal): Promise<readonly IncidentBrief[]> {
  const response = await request<{ items: IncidentBrief[] }>("/v1/incidents", { signal: signal ?? null });
  return response.items;
}

export async function loadIncident(
  incidentId: string,
  signal?: AbortSignal,
): Promise<IncidentDetail> {
  return request<IncidentDetail>(`/v1/incidents/${encodeURIComponent(incidentId)}`, {
    signal: signal ?? null,
  });
}

export async function loadChanges(signal?: AbortSignal): Promise<readonly ChangeVerificationSummary[]> {
  const response = await request<{ items: ChangeVerificationSummary[] }>("/v1/changes", { signal: signal ?? null });
  return response.items;
}

export async function loadChangeVerification(
  changeId: string,
  signal?: AbortSignal,
): Promise<ChangeVerificationDetail> {
  return request<ChangeVerificationDetail>(`/v1/changes/${encodeURIComponent(changeId)}/receipt`, {
    signal: signal ?? null,
  });
}

export async function createEmailNotificationRoute(
  monitorId: string,
  destination: string,
  idempotencyKey: string,
): Promise<NotificationRouteRecord> {
  const route = await request<Omit<NotificationRouteRecord, "latestDelivery">>(
    "/v1/notification-routes/email",
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "idempotency-key": idempotencyKey,
      },
      body: JSON.stringify({ monitorId, destination }),
    },
  );
  return { ...route, latestDelivery: null };
}

export interface TestIncidentResult {
  incident: IncidentBrief;
  notificationCount: number;
}

export async function createTestIncident(
  monitorId: string,
  idempotencyKey: string,
): Promise<TestIncidentResult> {
  return request<TestIncidentResult>("/v1/test-incidents", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "idempotency-key": idempotencyKey,
    },
    body: JSON.stringify({ monitorId }),
  });
}

export async function createService(name: string, idempotencyKey: string): Promise<ServiceSummary> {
  return request<ServiceSummary>("/v1/services", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "idempotency-key": idempotencyKey,
    },
    body: JSON.stringify({ name }),
  });
}

export interface CreateOperationInput {
  operationName: string;
  operationKey: string;
  environment: string;
  targetUrl: string;
  method: "GET" | "HEAD";
  expectedStatus: number;
  intervalSeconds: number;
  successCriteria: string;
}

export async function createOperation(
  serviceId: string,
  input: CreateOperationInput,
  idempotencyKey: string,
): Promise<CoverageRecord> {
  return request<CoverageRecord>(`/v1/services/${encodeURIComponent(serviceId)}/operations`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(input),
  });
}
